import { groupWith, append } from 'ramda';
import { TEDMixedType } from '../../../../../../../Types/GraphQLTypes';
import { getMixedValue } from '../../../../../../../Components/QueryComponents/tedMixedUtils';
import { findKey } from '../EkonomiskAnalysQueryUtils';
import {
  AnalysQueryDynamic_ekanalysresultatperanalys_columns as IColumnsData,
  AnalysQueryDynamic_ekanalysresultatperanalys_sums as ISumsData,
  AnalysQueryDynamic_ekanalysresultatperanalys_columns_fieldinfo as IFieldInfo,
} from './_QueryTypes/AnalysQueryDynamic';
import { DisplayType } from './EkonomiskAnalysQuery';

// intern variant av IRow, som bara används inom EkonomiskAnalysQuery
export interface IInternalRow {
  data: IInternalCell[];
  children?: IInternalRow[];
  sumValues?: string[];
}
// intern variant av ICell, som bara används inom filen
export interface IInternalCell {
  value: string;
  hasDetails: boolean;
  displayType?: 'percent' | 'currency';
  sumValue?: string;
  calculateData?: IOperatorData;
  fieldName?: string;
  hasChildValues: boolean;
}

interface IOperatorData {
  operator: string;
  operand1: string;
  operand2: string;
}

export const evaluateCalculatedColumns = (
  rows: IInternalRow[],
  isTotalSum?: boolean
) => {
  rows.forEach(row => {
    const cells = row.data;
    cells.forEach(cell => {
      if (cell.calculateData) {
        cell.value = String(calculateValue(cell.calculateData, row.data));
        delete cell.calculateData;
      }
    });
    if (row.children) {
      evaluateCalculatedColumns(row.children);
    }
  });
};

export const calculateValue = (
  operation: IOperatorData,
  row: IInternalCell[]
): number => {
  const fieldVal1 = getVal(operation.operand1, row);
  const fieldVal2 = getVal(operation.operand2, row);

  if (fieldVal1 === null || fieldVal2 === null) {
    return 0;
  }

  const val1 = parseFloat(fieldVal1) || 0;
  const val2 = parseFloat(fieldVal2) || 0;

  if (operation.operator === '-') {
    return val1 - val2;
  } else if (operation.operator === '%') {
    if (val2 === 0) {
      return 0;
    }

    return val1 / val2;
  }

  return 0;
};

export const getVal = (
  fieldName: string,
  row: IInternalCell[]
): string | null => {
  const val = row.find(c => c.fieldName === fieldName);
  return val ? val.value : null;
};

export const getFieldOperatorData = (
  fieldInfo: IFieldInfo[] | null
): IOperatorData | null => {
  if (!fieldInfo) {
    return null;
  }

  const operator = fieldInfo.find(f => f.name === 'FieldOperator');

  if (
    !operator ||
    !operator.value ||
    (getMixedValue(operator.value) !== '-' &&
      getMixedValue(operator.value) !== '%')
  ) {
    return null;
  }

  const operand1 = fieldInfo.find(f => f.name === 'AnvanderFieldname1');
  const operand2 = fieldInfo.find(f => f.name === 'AnvanderFieldname2');

  // Vi behöver 2 operander
  if (!operand1 || !operand2) {
    return null;
  }

  return {
    operator: getMixedValue(operator.value) as string,
    operand1: operand1.value ? (getMixedValue(operand1.value) as string) : '',
    operand2: operand2.value ? (getMixedValue(operand2.value) as string) : '',
  };
};

/**
 * Mutates rows by adding sumValues, used to get detailed analys
 */
export const setSumValues = (rows: IInternalRow[], parentValues: string[]) => {
  rows.forEach(row => {
    const sumValue = row.data[0].sumValue || '';

    const sumValues = append(sumValue, parentValues);
    row.sumValues = sumValues;

    if (row.children) {
      setSumValues(row.children, sumValues);
    }
  });
};

export const setData = (
  queryResult: IColumnsData | ISumsData,
  result: IInternalRow[],
  mergeWithData?: TEDMixedType | null,
  calculateData?: IOperatorData
) => {
  const data = queryResult.data;

  if (!data) {
    return;
  }

  const key = findKey(data);
  if (key === null) {
    return;
  }

  let hasData = false;
  if (queryResult.data !== null) {
    const data = getMixedValue(queryResult.data);
    if (Array.isArray(data)) {
      hasData = data.some((value: number | string | boolean | null) => {
        return value != null;
      });
    }
  }

  (data as unknown as Record<string, unknown[]>)[key].forEach((x: any, i: number) => {
    if (!result[i]) {
      result.push({
        data: [],
      });
    }

    let value = x != null ? x : '';

    const sumValue: string = value;
    if (mergeWithData) {
      const mergeValue = (mergeWithData as unknown as Record<string, unknown[]>)[key][i];
      if (value) {
        value = mergeValue ? `${value} - ${mergeValue}` : value;
      } else {
        value = mergeValue;
      }
    }

    result[i].data.push({
      value: value,
      sumValue,
      calculateData,
      fieldName: queryResult.fieldname || '',
      hasDetails: !calculateData,
      displayType: getDisplayType(calculateData),
      hasChildValues: hasData,
    });
  });
};

export const groupData = (
  analysData: IInternalRow[],
  numberOfColumns: number
): IInternalRow[] => {
  let groupList = groupWith(
    (a: IInternalRow, b: IInternalRow) => a.data[0].value === b.data[0].value
  )(analysData);

  let rows: IInternalRow[] = [];
  for (let i = 0; i < groupList.length; i++) {
    const groupRow = groupList[i];

    let row: IInternalRow = {
      data: [],
    };

    let children: IInternalRow[] = [];

    for (let j = 0; j < groupRow.length; j++) {
      let cells = groupRow[j].data;

      if (cells.length === numberOfColumns) {
        row.data = cells;
      } else {
        if (j === 0) {
          row.data = cells.slice(0, 1);
        }

        children.push({
          data: cells.slice(1),
        });
      }
    }

    if (children.length > 0) {
      let result = sumData(children, numberOfColumns - 1);
      row.data = row.data.concat(result);
      row.children = groupData(children, numberOfColumns);
    }

    rows.push(row);
  }

  return rows;
};

export const sumData = (
  data: IInternalRow[],
  numberOfColumns: number
): IInternalCell[] => {
  let result: IInternalCell[] = [];

  if (data.length === 0) {
    return result;
  }
  const length = data[0].data.length;
  for (let i = length - numberOfColumns; i < length; i++) {
    let sum = 0;
    let calculateData: IOperatorData | undefined;
    let hasData = false;
    data.forEach((x: IInternalRow) => {
      if (x.data[i].calculateData) {
        // This is a calculated column, we will calculate the value last
        calculateData = x.data[i].calculateData;
        return;
      }
      let cell = x.data[i];

      let value = parseFloat(cell.value);
      if (!isNaN(value)) {
        sum += value * 10000; // multiplying with 10k becuase of decimal errors
      }

      hasData = x.data[i].value !== '';
    });

    const value = (sum / 10000).toString();
    result.push({
      value: value,
      fieldName: data[0].data[i].fieldName,
      calculateData,
      hasDetails: !calculateData,
      displayType: getDisplayType(calculateData),
      hasChildValues: hasData,
    });
  }

  return result;
};

export const getDisplayType = (
  calculationData: IOperatorData | undefined
): DisplayType =>
  calculationData
    ? calculationData.operator === '%'
      ? 'percent'
      : 'currency'
    : 'currency';
